'use client'

import { motion } from 'framer-motion'
import React from 'react'

interface ApproachCardProps {
  title: string
  description: string
}

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
}

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
}

const ApproachCard: React.FC<ApproachCardProps> = ({ title, description }) => (
  <div className="bg-white p-6 sm:p-8 rounded-xl shadow-md ring-1 ring-border transition-transform will-change-transform hover:-translate-y-2 h-full">
    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">
      {title}
    </h3>
    <p className="text-sm sm:text-base text-gray-700">{description}</p>
  </div>
)

const TherapueticApproach: React.FC = () => {
  const approaches = [
    {
      title: 'Cognitive Behavioral Therapy (CBT)',
      description:
        'CBT focuses on identifying and changing unhelpful thought patterns and behaviors that contribute to emotional distress. This approach helps you develop practical skills to challenge negative thoughts, manage difficult emotions, and modify problematic behaviors, leading to improved mood and functioning.',
    },
    {
      title: 'Dialectical Behavior Therapy (DBT)',
      description:
        "DBT combines cognitive-behavioral techniques with mindfulness practices. It's particularly effective for managing intense emotions, improving interpersonal relationships, and developing distress tolerance. DBT provides concrete skills for emotional regulation and mindful awareness of the present moment.",
    },
    {
      title: 'Psychodynamic Therapy',
      description:
        'This approach explores how past experiences, often from childhood, influence current patterns of thought, emotion, and behavior. By increasing awareness of these unconscious processes, psychodynamic therapy helps you understand the root causes of recurring problems and develop more adaptive ways of relating to yourself and others.',
    },
    {
      title: 'Mindfulness-Based Therapies',
      description:
        "These approaches incorporate mindfulness practices to help you develop greater awareness of your thoughts, feelings, and bodily sensations without judgment. By learning to observe your experiences with acceptance and compassion, you can reduce stress, prevent relapse of depression, and respond more skillfully to life's challenges.",
    },
  ]

  return (
    <section
      id="therapeutic-approaches"
      className="py-12 sm:py-16 lg:py-20 px-4 bg-gray-50"
    >
      <div className="max-w-7xl mx-auto">
        {/* Heading */}
        <motion.div
          className="max-w-3xl mx-auto text-center mb-10 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={fadeIn}
        >
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-gray-900 mb-4 sm:mb-8">
            Our Therapeutic Approach: Tailored, Compassionate, and
            Evidence-Informed
          </h2>
          <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
            At ReACH Psychiatry, we don't believe in a one-size-fits-all
            approach to therapy. Our psychiatrists and therapists are trained in
            a variety of evidence-based modalities and will tailor their
            approach to your specific needs, personality, and goals. This
            "Compassion Meets Science" philosophy ensures you receive care that
            is both deeply empathetic and clinically effective.
          </p>
        </motion.div>

        {/* Cards */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={staggerContainer}
        >
          {approaches.map(({ title, description }) => (
            <motion.div key={title} variants={fadeIn}>
              <ApproachCard title={title} description={description} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

export default TherapueticApproach
